import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  FileEntry,
  FolderEntry,
  fileToRenderable,
  RenderableData,
} from "./fetchFiles";
import {
  TableHeader,
  getFileIcon,
  formatDate,
  getDisplayDate,
  isGreyedFile,
  isLockedFile,
  isSelectionBlockedFile,
} from "./rendering";
import { Datum } from "./datastructure";
import {
  ExtendedActionContext,
  getContextMenuActions,
  handleContextMenuAction,
} from "./actions";
import { ContextMenu } from "./contextmenu";
import {
  FolderIcon,
  FolderOpenIcon,
  CheckSquare,
  Square,
} from "@phosphor-icons/react";
import { FolderExpansionState } from "./useMockTreeExpansion";
import LockedFileIndicator from "@/components/common/LockedFileIndicator";
import { Tooltip } from "@mui/material";
import { useFrameWidth } from "@/hooks/useFrameWidth";
import { useViewerTimeZone } from "@/hooks/useViewerTimeZone";

interface SearchResultsViewProps {
  results?: FileEntry[];
  /** Folder search results to display above files */
  folderResults?: FolderEntry[];
  isLoading: boolean;
  onFileClick?: (file: FileEntry) => void;
  /** Callback when a file checkbox is clicked (selection toggle). Falls back to onFileClick if not provided. */
  onFileSelect?: (file: FileEntry) => void;
  /** Callback when a folder search result is clicked (checkbox in selection mode) */
  onFolderClick?: (folder: FolderEntry) => void;
  sortBy: "name" | "date";
  sortDirection: "asc" | "desc";
  onSortChange: (column: "name" | "date") => void;
  showAcrossDrives?: boolean;
  driveIdToName?: Map<string, string>;
  /** Show checkboxes for selection mode - clicking row toggles selection */
  showCheckboxes?: boolean;
  /** In selection mode, processing files are inert unless the host allows
   *  selecting them (the Documents page does, for bulk move/delete/open;
   *  pickers do not). Same knob as the tree's, so a locked row reads the
   *  same in the tree and in the search results of one page (#11856). */
  allowProcessingFileSelection?: boolean;
  /** Check if a file is selected (required when showCheckboxes is true) */
  isFileSelected?: (fileId: string) => boolean;
  /** Check if a folder is selected (for search folder results) */
  isFolderSelected?: (folder: FolderEntry) => boolean;
  /** Optional: display selected datums instead of search results */
  datums?: Datum[];
  /** Callback when a datum row is clicked (folder navigation or file deselect) */
  onDatumClick?: (datum: Datum) => void;
  /** Callback when a datum checkbox is clicked (always deselects) */
  onDatumDeselect?: (datum: Datum) => void;
  /** Check if a datum is directly selected (not just in selected context). Used for conditional checkbox rendering. */
  isDatumSelected?: (datumId: string) => boolean;
  /** Callback when a search-selected file is clicked for deselection (used in combined mode) */
  onSearchFileDeselect?: (file: FileEntry) => void;
  /** Callback to toggle select/deselect all visible search results */
  onToggleSelectAll?: () => void;
  /** Callback to toggle inline expansion of a folder */
  onFolderToggle?: (filePath: string[], driveId: string | null) => void;
  /** Check if a folder is currently expanded inline */
  isFolderExpanded?: (filePath: string[], driveId: string | null) => boolean;
  /** Get expansion state (children, loading) for an expanded folder */
  getFolderExpansion?: (
    filePath: string[],
    driveId: string | null
  ) => FolderExpansionState | undefined;
  /**
   * Context for the shared context-menu action registry. Which actions a row
   * offers is decided by the registry from the row's datum and this context —
   * not by this component — so search results and the file tree cannot drift
   * apart again (#10889). Surfaces that only pick documents pass
   * `isPicker: true`; a row whose datum yields no actions renders no menu.
   */
  actionContext: ExtendedActionContext;
  /** Narrow-frame date treatment, mirroring FiletreeRenderer (#11742). */
  narrowDate?: "compact" | "hidden";
  /** Rows only, no Name/Date header — for a list that sits above the tree,
   *  whose header the tree already draws (the picker's pinned selection). */
  hideHeader?: boolean;
}

/**
 * Build the tree datum a search row stands for, so the shared action registry
 * can decide the row's menu.
 *
 * Search entries carry API-shaped paths (no synthetic drive prefix — the drive
 * is a sibling field), which is why the search action context sets neither a
 * filetree nor across-drives mode: `path[0]` is the real drive-root segment
 * that read-only detection anchors on.
 */
const fileToDatum = (file: FileEntry): Datum =>
  new Datum([...file.filePath, file.fileName], "file", fileToRenderable(file));

const folderToDatum = (folder: FolderEntry): Datum =>
  new Datum(folder.filePath, "folder", {
    type: "folder",
    documentDate: folder.documentDate,
    driveId: folder.driveId,
  });

/** One rendered row of the results: a file, its indent, and for a lineage's
 * head the members folded beneath it. `elsewhereCount` marks the trailing
 * note row naming members outside this search. */
interface DisplayRow {
  file: FileEntry;
  depth: number;
  hiddenVersions: FileEntry[];
  elsewhereCount?: number;
}

/**
 * The pill on a lineage's row: how many other versions the document has. It
 * expands the ones this search returned; any beyond those are named under
 * the expanded rows, since a count that changed with the query would read as
 * the document changing.
 */
function VersionsPill({
  hiddenCount,
  otherCount,
  expanded,
  onToggle,
}: {
  hiddenCount: number;
  otherCount: number;
  expanded: boolean;
  onToggle?: () => void;
}) {
  const total = Math.max(otherCount, hiddenCount);
  if (hiddenCount > 0 && onToggle) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        aria-expanded={expanded}
        className={`shrink-0 rounded-full px-1.5 py-px text-[11px] leading-4 cursor-pointer transition-colors ${
          expanded
            ? "bg-gray-800 text-white"
            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
        }`}
      >
        {total} other version{total === 1 ? "" : "s"}
      </button>
    );
  }
  if (otherCount > 0) {
    return (
      <Tooltip
        title={`${otherCount} other version${otherCount === 1 ? "" : "s"} not in these results`}
        arrow
        placement="top"
      >
        <span className="shrink-0 rounded-full px-1.5 py-px text-[11px] leading-4 border border-gray-200 text-gray-500">
          +{otherCount}
        </span>
      </Tooltip>
    );
  }
  return null;
}

/** Sort folders by current sort settings */
function sortFolders(
  folders: FolderEntry[],
  sortBy: "name" | "date",
  sortDirection: "asc" | "desc"
): FolderEntry[] {
  return [...folders].sort((a, b) => {
    const nameA = a.filePath[a.filePath.length - 1] || "";
    const nameB = b.filePath[b.filePath.length - 1] || "";
    const cmp =
      sortBy === "date"
        ? (a.documentDate || "").localeCompare(b.documentDate || "")
        : nameA.localeCompare(nameB);
    return sortDirection === "desc" ? -cmp : cmp;
  });
}

/** Sort files by current sort settings */
function sortFiles(
  files: FileEntry[],
  sortBy: "name" | "date",
  sortDirection: "asc" | "desc"
): FileEntry[] {
  const getTimestamp = (file: FileEntry) => {
    const dateStr = getDisplayDate(fileToRenderable(file));
    return dateStr ? new Date(dateStr).getTime() : 0;
  };
  return [...files].sort((a, b) => {
    const cmp =
      sortBy === "date"
        ? getTimestamp(a) - getTimestamp(b)
        : a.fileName.localeCompare(b.fileName);
    return sortDirection === "desc" ? -cmp : cmp;
  });
}

export const SearchResultsView: React.FC<SearchResultsViewProps> = ({
  results,
  folderResults,
  isLoading,
  onFileClick,
  onFileSelect,
  onFolderClick,
  sortBy,
  sortDirection,
  onSortChange,
  showAcrossDrives = false,
  driveIdToName,
  showCheckboxes = false,
  allowProcessingFileSelection = false,
  isFileSelected,
  isFolderSelected,
  datums,
  onDatumClick,
  onDatumDeselect,
  isDatumSelected,
  onSearchFileDeselect,
  onToggleSelectAll,
  onFolderToggle,
  isFolderExpanded,
  getFolderExpansion,
  actionContext,
  narrowDate = "compact",
  hideHeader = false,
}) => {
  // The zone an upload instant is read in. A document date is a calendar
  // date and ignores it (#11055).
  const viewerZone = useViewerTimeZone();

  // Same frame rule as FiletreeRenderer (#11742): search results render into
  // the identical narrow containers (the phone picker dialog most of all), so
  // a narrow frame drops the 400px floor, compacts or hides the date column,
  // and lets the table-fixed name cells do the ellipsizing they already do.
  const { frameRef, frameWidth } = useFrameWidth<HTMLDivElement>();
  const narrow = frameWidth !== null && frameWidth < 420;
  const hideDate = narrow && narrowDate === "hidden";
  const dateFormat: "medium" | "numeric" = narrow ? "numeric" : "medium";
  const dateTdClass = `py-1 px-2 text-right text-sm text-gray-500 ${narrow ? "w-24" : "w-32"} whitespace-nowrap`;
  // Path sub-rows span name+date; with the date column standing down they
  // span the name column alone.
  const pathColSpan = hideDate ? 1 : 2;

  // One menu for both files and folders — the registry decides its contents
  // from the datum, so there is nothing surface-specific left to duplicate.
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    datum: Datum;
  } | null>(null);

  // Determine which mode we're in
  // If both datums and results are provided, we're in combined mode (show selected)
  const isDatumsMode = datums !== undefined;
  const isCombinedMode = datums !== undefined && results !== undefined;

  // Sort results (FileEntry[]) when using search results mode
  const sortedResults = useMemo(
    () => (results ? sortFiles(results, sortBy, sortDirection) : []),
    [results, sortBy, sortDirection]
  );

  // Copies and revisions of one document group under one row (#11956). The
  // row is the lineage's latest member when the search returned it, else the
  // first member in sort order, and it sits where that member falls in the
  // sort; the others sit behind a pill that expands them beneath it.
  // Selection and Select All still count every file, since `results` itself
  // is untouched. Combined mode lists the user's own selection and is left
  // flat.
  const [expandedLineages, setExpandedLineages] = useState<Set<number>>(
    () => new Set()
  );
  const displayRows = useMemo((): DisplayRow[] => {
    const rows: DisplayRow[] = [];
    if (isCombinedMode) {
      return sortedResults.map((file) => ({
        file,
        depth: 0,
        hiddenVersions: [],
      }));
    }
    const byLineage = new Map<number, FileEntry[]>();
    for (const file of sortedResults) {
      if (file.lineageId == null) continue;
      const group = byLineage.get(file.lineageId);
      if (group) group.push(file);
      else byLineage.set(file.lineageId, [file]);
    }
    for (const file of sortedResults) {
      const group =
        file.lineageId == null ? undefined : byLineage.get(file.lineageId);
      if (!group || group.length < 2 || file.lineageId == null) {
        rows.push({ file, depth: 0, hiddenVersions: [] });
        continue;
      }
      const head = group.find((f) => f.isLatestVersion) ?? group[0];
      // The group renders once, at its head's own place in the sort.
      if (file.fileId !== head.fileId) continue;
      const hidden = group.filter((f) => f.fileId !== head.fileId);
      rows.push({ file: head, depth: 0, hiddenVersions: hidden });
      if (expandedLineages.has(file.lineageId)) {
        for (const member of hidden) {
          rows.push({ file: member, depth: 1, hiddenVersions: [] });
        }
        const elsewhere = (head.versionCount ?? 0) - hidden.length;
        if (elsewhere > 0) {
          rows.push({
            file: head,
            depth: 1,
            hiddenVersions: [],
            elsewhereCount: elsewhere,
          });
        }
      }
    }
    return rows;
  }, [sortedResults, expandedLineages, isCombinedMode]);
  const toggleLineage = useCallback((lineageId: number) => {
    setExpandedLineages((prev) => {
      const next = new Set(prev);
      if (next.has(lineageId)) next.delete(lineageId);
      else next.add(lineageId);
      return next;
    });
  }, []);

  // Sort folder search results
  const sortedFolderResults = useMemo(
    () =>
      folderResults ? sortFolders(folderResults, sortBy, sortDirection) : [],
    [folderResults, sortBy, sortDirection]
  );

  // Sort datums when using datums mode (for showing selected items)
  const sortedDatums = useMemo(() => {
    if (!datums) return [];
    const getTimestamp = (datum: Datum) => {
      if (!datum.payload) return 0;
      const dateStr = getDisplayDate(datum.payload as RenderableData);
      return dateStr ? new Date(dateStr).getTime() : 0;
    };

    return [...datums].sort((a, b) => {
      const cmp =
        sortBy === "date"
          ? getTimestamp(a) - getTimestamp(b)
          : (a.name || "").localeCompare(b.name || "");
      return sortDirection === "desc" ? -cmp : cmp;
    });
  }, [datums, sortBy, sortDirection]);

  // Check if all selectable search results are selected (for Select All icon state)
  // Only considers selectable files — processing and locked files are not;
  // a failed file is (#13887)
  const allResultsSelected = useMemo(() => {
    const selectableFiles = results?.filter(
      (f) => !isSelectionBlockedFile(fileToRenderable(f))
    );
    const hasResults =
      (selectableFiles && selectableFiles.length > 0) ||
      (folderResults && folderResults.length > 0);
    if (!hasResults) return false;
    const allFilesSelected =
      !selectableFiles || selectableFiles.length === 0 || !isFileSelected
        ? true
        : selectableFiles.every((file) => isFileSelected(file.fileId));
    const allFoldersSelected =
      !folderResults || folderResults.length === 0 || !isFolderSelected
        ? true
        : folderResults.every((folder) => isFolderSelected(folder));
    return allFilesSelected && allFoldersSelected;
  }, [results, isFileSelected, folderResults, isFolderSelected]);

  const openContextMenu = useCallback((e: React.MouseEvent, datum: Datum) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, datum });
  }, []);

  const menuActions = useMemo(
    () =>
      contextMenu
        ? getContextMenuActions(contextMenu.datum, actionContext)
        : [],
    [contextMenu, actionContext]
  );

  const runMenuAction = useCallback(
    async (action: string) => {
      if (!contextMenu) return;
      const datum = contextMenu.datum;
      setContextMenu(null);
      await handleContextMenuAction(action, datum, actionContext);
    },
    [contextMenu, actionContext]
  );

  // Close context menu on click outside or Escape
  useEffect(() => {
    if (!contextMenu) return;
    const handleClick = () => setContextMenu(null);
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setContextMenu(null);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [contextMenu]);

  /**
   * Recursively render child folders and files from an inline expansion.
   * Used for both folder search results and datum folder expansion.
   */
  const renderChildItems = (
    folders: FolderEntry[],
    files: FileEntry[],
    depth: number
  ): React.ReactNode[] => {
    const indent = depth * 20;
    const rows: React.ReactNode[] = [];
    const childFolders = sortFolders(folders, sortBy, sortDirection);
    const childFiles = sortFiles(files, sortBy, sortDirection);

    for (const folder of childFolders) {
      const folderName = folder.filePath[folder.filePath.length - 1] || "";
      const formattedDate = formatDate(
        folder.documentDate || "",
        viewerZone,
        dateFormat
      );
      const expanded = isFolderExpanded?.(folder.filePath, folder.driveId);
      const isSelected = !!isFolderSelected?.(folder);
      const key = `child-folder-d${depth}-${folder.driveId || "null"}-${JSON.stringify(folder.filePath)}`;

      rows.push(
        <tr
          key={key}
          className={`cursor-pointer select-none hover:bg-gray-100 ${isSelected ? "bg-[#F7F8FF]" : ""}`}
          onClick={(e) => {
            if ((e.ctrlKey || e.metaKey) && onFolderClick) {
              onFolderClick(folder);
            } else {
              onFolderToggle?.(folder.filePath, folder.driveId);
            }
          }}
          onContextMenu={(e) => openContextMenu(e, folderToDatum(folder))}
        >
          {showCheckboxes && (
            <td className="py-1 pl-3 pr-1 w-8">
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onFolderClick?.(folder);
                }}
                className="cursor-pointer flex items-center justify-center"
              >
                {isSelected ? (
                  <CheckSquare
                    size={16}
                    weight="fill"
                    style={{ color: "#2A3DCB" }}
                  />
                ) : (
                  <Square size={16} className="text-gray-300" />
                )}
              </div>
            </td>
          )}
          <td className="py-1 px-2 overflow-hidden">
            <div
              className="flex items-center gap-2"
              style={{ paddingLeft: indent }}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                {expanded ? (
                  <FolderOpenIcon
                    size={16}
                    weight="regular"
                    className="text-gray-600"
                  />
                ) : (
                  <FolderIcon
                    size={16}
                    weight="regular"
                    className="text-gray-600"
                  />
                )}
              </div>
              <div className="min-w-0 overflow-hidden">
                <div
                  className="truncate"
                  style={isSelected ? { color: "#2A3DCB" } : undefined}
                >
                  {folderName}
                </div>
              </div>
            </div>
          </td>
          {!hideDate && <td className={dateTdClass}>{formattedDate}</td>}
        </tr>
      );

      if (expanded) {
        const expansion = getFolderExpansion?.(folder.filePath, folder.driveId);
        if (expansion?.isLoading) {
          rows.push(
            <tr key={`${key}-loading`}>
              {showCheckboxes && <td className="w-8" />}
              <td className="py-1 px-2" colSpan={pathColSpan}>
                <div
                  className="flex items-center gap-2 text-gray-400 text-sm"
                  style={{ paddingLeft: indent + 20 }}
                >
                  <div className="animate-spin w-3 h-3 border border-gray-300 border-t-blue-600 rounded-full" />
                  Loading...
                </div>
              </td>
            </tr>
          );
        } else if (expansion) {
          if (expansion.folders.length === 0 && expansion.files.length === 0) {
            rows.push(
              <tr key={`${key}-empty`}>
                {showCheckboxes && <td className="w-8" />}
                <td className="py-1 px-2" colSpan={pathColSpan}>
                  <div
                    className="text-gray-400 text-sm italic"
                    style={{ paddingLeft: indent + 20 }}
                  >
                    This folder is empty
                  </div>
                </td>
              </tr>
            );
          } else {
            rows.push(
              ...renderChildItems(expansion.folders, expansion.files, depth + 1)
            );
          }
        }
      }
    }

    for (const file of childFiles) {
      const payload = fileToRenderable(file);
      const formattedDate = formatDate(
        getDisplayDate(payload) || "",
        viewerZone,
        dateFormat
      );
      const isSelected =
        !!isFileSelected?.(file.fileId) ||
        !!isFolderSelected?.({
          filePath: file.filePath,
          driveId: file.driveId || null,
          documentDate: "",
        });
      const isDisabled =
        showCheckboxes &&
        isSelectionBlockedFile(payload) &&
        !allowProcessingFileSelection;
      const dimmed = isGreyedFile(payload);

      rows.push(
        <tr
          key={`child-file-d${depth}-${file.fileId}`}
          className={`select-none ${isDisabled ? `cursor-default ${dimmed ? "opacity-50" : ""}` : `cursor-pointer hover:bg-gray-100 ${dimmed ? "opacity-50" : ""}`} ${isSelected ? "bg-[#F7F8FF]" : ""}`}
          onClick={(e) => {
            if ((e.ctrlKey || e.metaKey) && onFileSelect) {
              onFileSelect(file);
              return;
            }
            if (isDisabled) return;
            onFileClick?.(file);
          }}
          onContextMenu={(e) => openContextMenu(e, fileToDatum(file))}
        >
          {showCheckboxes && (
            <td className="py-1 pl-3 pr-1 w-8">
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  if (isDisabled) return;
                  (onFileSelect ?? onFileClick)?.(file);
                }}
                className={`${isDisabled ? "cursor-default" : "cursor-pointer"} flex items-center justify-center`}
              >
                {isSelected ? (
                  <CheckSquare
                    size={16}
                    weight="fill"
                    style={{ color: "#2A3DCB" }}
                  />
                ) : (
                  <Square size={16} className="text-gray-300" />
                )}
              </div>
            </td>
          )}
          <td className="py-1 px-2 overflow-hidden">
            <div
              className="flex items-center gap-2"
              style={{ paddingLeft: indent }}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                {getFileIcon(payload, dimmed)}
              </div>
              <div className="min-w-0 overflow-hidden">
                {/* The lock mark belongs to the name line, after the name,
                    which truncates while the mark does not (#11856). */}
                <div className="flex min-w-0 items-center gap-1.5">
                  <div
                    className={`truncate ${dimmed ? "text-gray-400" : ""}`}
                    style={
                      isSelected && !dimmed ? { color: "#2A3DCB" } : undefined
                    }
                  >
                    {file.fileName}
                  </div>
                  {isLockedFile(payload) && (
                    <LockedFileIndicator status={file.status} />
                  )}
                </div>
              </div>
            </div>
          </td>
          {!hideDate && <td className={dateTdClass}>{formattedDate}</td>}
        </tr>
      );
    }

    return rows;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin w-6 h-6 border-2 border-gray-300 border-t-blue-600 rounded-full" />
          <span className="text-sm">Loading...</span>
        </div>
      </div>
    );
  }

  // Empty state check
  const isEmpty = isCombinedMode
    ? sortedDatums.length === 0 &&
      sortedResults.length === 0 &&
      sortedFolderResults.length === 0
    : isDatumsMode
      ? sortedDatums.length === 0
      : sortedResults.length === 0 && sortedFolderResults.length === 0;

  if (isEmpty) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <p className="text-sm">
            {isDatumsMode ? "No items selected" : "No results found"}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {isDatumsMode
              ? "Select files or folders to see them here"
              : "Try a different search term"}
          </p>
        </div>
      </div>
    );
  }

  return (
    // The measuring wrapper carries no min-width of its own — the observer
    // must read the FRAME's width, which the min-width below would mask.
    <div ref={frameRef} className="w-full h-full min-w-0">
      <div
        className={`${narrow ? "min-w-0" : "min-w-[400px]"} w-full h-full px-4 pt-0 pb-2 text-[14px] flex flex-col`}
      >
        {!hideHeader && (
          <TableHeader
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSortChange={onSortChange}
            narrow={narrow}
            narrowDate={narrowDate}
          />
        )}
        {showCheckboxes && !isDatumsMode && onToggleSelectAll && (
          <div className="flex items-center gap-3 py-1.5 px-1">
            <button
              onClick={onToggleSelectAll}
              className="flex items-center gap-1 text-sm hover:opacity-80 transition-opacity"
              style={{ color: "#2A3DCB" }}
            >
              {allResultsSelected ? (
                <CheckSquare size={14} weight="fill" />
              ) : (
                <Square size={14} />
              )}
              Select All
            </button>
          </div>
        )}
        <div className="w-full border border-gray-100 overflow-auto flex-1 min-h-0">
          <table className="w-full table-fixed">
            <tbody>
              {/* Render datums (from tree selection) */}
              {isDatumsMode &&
                sortedDatums.map((datum) => {
                  const payload = datum.payload as RenderableData | undefined;
                  const isFolder = datum.type === "folder";
                  const dimmed = isGreyedFile(payload);
                  const formattedDate = payload
                    ? formatDate(
                        getDisplayDate(payload) || "",
                        viewerZone,
                        dateFormat
                      )
                    : "";
                  // Build path string (exclude item name from path display)
                  const parentPath = datum.path.slice(0, -1);
                  const driveId = payload?.driveId;
                  const driveName = driveId && driveIdToName?.get(driveId);
                  const basePath = showAcrossDrives
                    ? parentPath.slice(1).join(" / ") // Skip drive ID in path
                    : parentPath.join(" / ");
                  const pathString =
                    showAcrossDrives && driveName
                      ? `${driveName}${basePath ? ": " + basePath : ""}`
                      : basePath;

                  // For folder datums, compute the API-level path for expansion
                  const datumApiPath =
                    isFolder && showAcrossDrives && driveId
                      ? datum.path.slice(1)
                      : datum.path;
                  const datumExpanded =
                    isFolder &&
                    isFolderExpanded?.(datumApiPath, driveId ?? null);

                  const datumIsSelected = isDatumSelected?.(datum.id) ?? true;

                  return (
                    <React.Fragment key={datum.id}>
                      <tr
                        className={`cursor-pointer select-none hover:bg-gray-100 ${dimmed ? "opacity-50" : ""} ${datumIsSelected ? "bg-[#F7F8FF]" : ""}`}
                        onClick={() => {
                          if (isFolder && onFolderToggle) {
                            onFolderToggle(datumApiPath, driveId ?? null);
                          } else {
                            onDatumClick?.(datum);
                          }
                        }}
                        onContextMenu={(e) => openContextMenu(e, datum)}
                      >
                        {showCheckboxes && (
                          <td className="py-1 pl-3 pr-1 w-8">
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                (onDatumDeselect ?? onDatumClick)?.(datum);
                              }}
                              className="cursor-pointer flex items-center justify-center"
                            >
                              {datumIsSelected ? (
                                <CheckSquare
                                  size={16}
                                  weight="fill"
                                  style={{ color: "#2A3DCB" }}
                                />
                              ) : (
                                <Square size={16} className="text-gray-300" />
                              )}
                            </div>
                          </td>
                        )}
                        <td className="py-1 px-2 overflow-hidden">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 flex items-center justify-center">
                              {isFolder ? (
                                datumExpanded ? (
                                  <FolderOpenIcon
                                    size={16}
                                    weight="regular"
                                    className="text-gray-600"
                                  />
                                ) : (
                                  <FolderIcon
                                    size={16}
                                    weight="regular"
                                    className="text-gray-600"
                                  />
                                )
                              ) : payload ? (
                                getFileIcon(payload, dimmed)
                              ) : null}
                            </div>
                            <div className="min-w-0 overflow-hidden">
                              <div
                                className={`truncate ${dimmed ? "text-gray-400" : ""}`}
                                style={
                                  datumIsSelected && !dimmed
                                    ? { color: "#2A3DCB" }
                                    : undefined
                                }
                              >
                                {datum.name}
                                {isFolder && (
                                  <span className="text-gray-400 ml-1">
                                    (folder)
                                  </span>
                                )}
                              </div>
                              {pathString && (
                                <div className="text-xs text-gray-400 truncate">
                                  {pathString}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        {!hideDate && (
                          <td className={dateTdClass}>{formattedDate}</td>
                        )}
                      </tr>
                      {isFolder &&
                        datumExpanded &&
                        (() => {
                          const expansion = getFolderExpansion?.(
                            datumApiPath,
                            driveId ?? null
                          );
                          if (expansion?.isLoading) {
                            return (
                              <tr>
                                {showCheckboxes && <td className="w-8" />}
                                <td className="py-1 px-2" colSpan={pathColSpan}>
                                  <div
                                    className="flex items-center gap-2 text-gray-400 text-sm"
                                    style={{ paddingLeft: 20 }}
                                  >
                                    <div className="animate-spin w-3 h-3 border border-gray-300 border-t-blue-600 rounded-full" />
                                    Loading...
                                  </div>
                                </td>
                              </tr>
                            );
                          }
                          if (expansion) {
                            if (
                              expansion.folders.length === 0 &&
                              expansion.files.length === 0
                            ) {
                              return (
                                <tr>
                                  {showCheckboxes && <td className="w-8" />}
                                  <td
                                    className="py-1 px-2"
                                    colSpan={pathColSpan}
                                  >
                                    <div
                                      className="text-gray-400 text-sm italic"
                                      style={{ paddingLeft: 20 }}
                                    >
                                      This folder is empty
                                    </div>
                                  </td>
                                </tr>
                              );
                            }
                            return renderChildItems(
                              expansion.folders,
                              expansion.files,
                              1
                            );
                          }
                          return null;
                        })()}
                    </React.Fragment>
                  );
                })}
              {/* Render folder search results (above files) */}
              {(!isDatumsMode || isCombinedMode) &&
                sortedFolderResults.map((folder) => {
                  const folderName =
                    folder.filePath[folder.filePath.length - 1] || "";
                  const parentPath = folder.filePath.slice(0, -1);
                  const driveName =
                    folder.driveId && driveIdToName?.get(folder.driveId);
                  const basePath = parentPath.join(" / ");
                  const pathString =
                    showAcrossDrives && driveName
                      ? `${driveName}${basePath ? ": " + basePath : ""}`
                      : basePath;
                  const formattedDate = formatDate(
                    folder.documentDate || "",
                    viewerZone,
                    dateFormat
                  );
                  const isSelected =
                    showCheckboxes && isFolderSelected?.(folder);
                  const expanded = isFolderExpanded?.(
                    folder.filePath,
                    folder.driveId
                  );
                  const key = `folder-${folder.driveId || "null"}-${JSON.stringify(folder.filePath)}`;

                  return (
                    <React.Fragment key={key}>
                      <tr
                        className={`cursor-pointer select-none hover:bg-gray-100 ${isSelected ? "bg-[#F7F8FF]" : ""}`}
                        onClick={(e) => {
                          if ((e.ctrlKey || e.metaKey) && onFolderClick) {
                            onFolderClick(folder);
                          } else if (onFolderToggle) {
                            onFolderToggle(folder.filePath, folder.driveId);
                          } else {
                            onFolderClick?.(folder);
                          }
                        }}
                        onContextMenu={(e) =>
                          openContextMenu(e, folderToDatum(folder))
                        }
                      >
                        {showCheckboxes && (
                          <td className="py-1 pl-3 pr-1 w-8">
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                onFolderClick?.(folder);
                              }}
                              className="cursor-pointer flex items-center justify-center"
                            >
                              {isSelected ? (
                                <CheckSquare
                                  size={16}
                                  weight="fill"
                                  style={{ color: "#2A3DCB" }}
                                />
                              ) : (
                                <Square size={16} className="text-gray-300" />
                              )}
                            </div>
                          </td>
                        )}
                        <td className="py-1 px-2 overflow-hidden">
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 flex items-center justify-center">
                              {expanded ? (
                                <FolderOpenIcon
                                  size={16}
                                  weight="regular"
                                  className="text-gray-600"
                                />
                              ) : (
                                <FolderIcon
                                  size={16}
                                  weight="regular"
                                  className="text-gray-600"
                                />
                              )}
                            </div>
                            <div className="min-w-0 overflow-hidden">
                              <div
                                className="truncate"
                                style={
                                  isSelected ? { color: "#2A3DCB" } : undefined
                                }
                              >
                                {folderName}
                                <span className="text-gray-400 ml-1">
                                  (folder)
                                </span>
                              </div>
                              {pathString && (
                                <div className="text-xs text-gray-400 truncate">
                                  {pathString}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        {!hideDate && (
                          <td className={dateTdClass}>{formattedDate}</td>
                        )}
                      </tr>
                      {expanded &&
                        (() => {
                          const expansion = getFolderExpansion?.(
                            folder.filePath,
                            folder.driveId
                          );
                          if (expansion?.isLoading) {
                            return (
                              <tr key={`${key}-loading`}>
                                {showCheckboxes && <td className="w-8" />}
                                <td className="py-1 px-2" colSpan={pathColSpan}>
                                  <div
                                    className="flex items-center gap-2 text-gray-400 text-sm"
                                    style={{ paddingLeft: 20 }}
                                  >
                                    <div className="animate-spin w-3 h-3 border border-gray-300 border-t-blue-600 rounded-full" />
                                    Loading...
                                  </div>
                                </td>
                              </tr>
                            );
                          }
                          if (expansion) {
                            if (
                              expansion.folders.length === 0 &&
                              expansion.files.length === 0
                            ) {
                              return (
                                <tr key={`${key}-empty`}>
                                  {showCheckboxes && <td className="w-8" />}
                                  <td
                                    className="py-1 px-2"
                                    colSpan={pathColSpan}
                                  >
                                    <div
                                      className="text-gray-400 text-sm italic"
                                      style={{ paddingLeft: 20 }}
                                    >
                                      This folder is empty
                                    </div>
                                  </td>
                                </tr>
                              );
                            }
                            return renderChildItems(
                              expansion.folders,
                              expansion.files,
                              1
                            );
                          }
                          return null;
                        })()}
                    </React.Fragment>
                  );
                })}
              {/* Render FileEntry results (from search or search-selected files) */}
              {(!isDatumsMode || isCombinedMode) &&
                displayRows.map(
                  ({ file, depth, hiddenVersions, elsewhereCount }) => {
                    if (elsewhereCount) {
                      return (
                        <tr key={`${file.fileId}-elsewhere`}>
                          {showCheckboxes && <td className="w-8" />}
                          <td className="py-1 px-2" colSpan={pathColSpan}>
                            <div
                              className="text-xs text-gray-400"
                              style={{ paddingLeft: depth * 20 + 24 }}
                            >
                              {elsewhereCount} more version
                              {elsewhereCount === 1 ? "" : "s"} not in these
                              results
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    const payload = fileToRenderable(file);
                    const formattedDate = formatDate(
                      getDisplayDate(payload) || "",
                      viewerZone,
                      dateFormat
                    );
                    const basePath = file.filePath.join(" / ");
                    const driveName =
                      file.driveId && driveIdToName?.get(file.driveId);
                    const pathString =
                      showAcrossDrives && driveName
                        ? `${driveName}: ${basePath}`
                        : basePath;
                    // In combined mode, files are always selected (for deselection)
                    // In regular search mode, the file is selected if it's chosen
                    // directly or inherits selection from an ancestor folder.
                    const isSelected = isCombinedMode
                      ? true
                      : showCheckboxes &&
                        (isFileSelected?.(file.fileId) ||
                          isFolderSelected?.({
                            filePath: file.filePath,
                            driveId: file.driveId || null,
                            documentDate: "",
                          }));

                    // In selection mode, processing and locked files are not
                    // interactive unless the host allows it (the Documents
                    // page does). A failed file is (#13887).
                    const isDisabled =
                      showCheckboxes &&
                      isSelectionBlockedFile(payload) &&
                      !allowProcessingFileSelection;
                    const dimmed = isGreyedFile(payload);

                    return (
                      <tr
                        key={file.fileId}
                        className={`select-none ${isDisabled ? `cursor-default ${dimmed ? "opacity-50" : ""}` : `cursor-pointer hover:bg-gray-100 ${dimmed ? "opacity-50" : ""}`} ${isSelected ? "bg-[#F7F8FF]" : ""}`}
                        onClick={(e) => {
                          if (
                            (e.ctrlKey || e.metaKey) &&
                            onFileSelect &&
                            !isCombinedMode
                          ) {
                            onFileSelect(file);
                            return;
                          }
                          if (isDisabled) return;
                          isCombinedMode
                            ? onSearchFileDeselect?.(file)
                            : onFileClick?.(file);
                        }}
                        onContextMenu={(e) =>
                          openContextMenu(e, fileToDatum(file))
                        }
                      >
                        {showCheckboxes && (
                          <td className="py-1 pl-3 pr-1 w-8">
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isDisabled) return;
                                isCombinedMode
                                  ? onSearchFileDeselect?.(file)
                                  : (onFileSelect ?? onFileClick)?.(file);
                              }}
                              className={`${isDisabled ? "cursor-default" : "cursor-pointer"} flex items-center justify-center`}
                            >
                              {isSelected ? (
                                <CheckSquare
                                  size={16}
                                  weight="fill"
                                  style={{ color: "#2A3DCB" }}
                                />
                              ) : (
                                <Square size={16} className="text-gray-300" />
                              )}
                            </div>
                          </td>
                        )}
                        <td className="py-1 px-2 overflow-hidden">
                          <div
                            className="flex items-center gap-2"
                            style={
                              depth ? { paddingLeft: depth * 20 } : undefined
                            }
                          >
                            <div className="w-4 h-4 flex items-center justify-center">
                              {getFileIcon(payload, dimmed)}
                            </div>
                            <div className="min-w-0 overflow-hidden">
                              <div className="flex min-w-0 items-center gap-1.5">
                                <div
                                  className={`truncate ${dimmed ? "text-gray-400" : ""}`}
                                  style={
                                    isSelected && !dimmed
                                      ? { color: "#2A3DCB" }
                                      : undefined
                                  }
                                >
                                  {file.fileName}
                                </div>
                                {isLockedFile(payload) && (
                                  <LockedFileIndicator status={file.status} />
                                )}
                                {depth === 0 && (
                                  <VersionsPill
                                    hiddenCount={hiddenVersions.length}
                                    otherCount={file.versionCount ?? 0}
                                    expanded={
                                      file.lineageId != null &&
                                      expandedLineages.has(file.lineageId)
                                    }
                                    onToggle={
                                      file.lineageId != null
                                        ? () => toggleLineage(file.lineageId!)
                                        : undefined
                                    }
                                  />
                                )}
                              </div>
                              {pathString && (
                                <div className="text-xs text-gray-400 truncate">
                                  {pathString}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        {!hideDate && (
                          <td className={dateTdClass}>{formattedDate}</td>
                        )}
                      </tr>
                    );
                  }
                )}
            </tbody>
          </table>
        </div>

        {contextMenu && menuActions.length > 0 && (
          <ContextMenu
            contextMenu={{
              visible: true,
              x: contextMenu.x,
              y: contextMenu.y,
              itemId: contextMenu.datum.id,
            }}
            actions={menuActions}
            onMenuAction={runMenuAction}
          />
        )}
      </div>
    </div>
  );
};
